import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAgentTeamStore } from '../../store/agentTeamStore';
import type { AgentTeamSession } from '../../types/agentTeam';
import { formatRelativeTime } from '../../utils/timeFormat';

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface AgentTeamSessionListProps {
  workspaceId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const AgentTeamSessionList: React.FC<AgentTeamSessionListProps> = ({
  workspaceId,
  isOpen,
  onClose,
}) => {
  const {
    sessions,
    session: currentSession,
    loadSession,
    deleteSession,
    fetchSessions,
    loading,
    sessionsLoaded,
  } = useAgentTeamStore();

  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<AgentTeamSession | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleSessionClick = useCallback(async (session: AgentTeamSession) => {
    if (loading || session.id === currentSession?.id) {
      onClose();
      return;
    }
    try {
      await loadSession(session.id);
      onClose();
    } catch (error) {
      console.error('切换会话失败:', error);
    }
  }, [currentSession, loading, loadSession, onClose]);

  const handleDeleteClick = useCallback((session: AgentTeamSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!sessionToDelete) return;
    setDeletingSessionId(sessionToDelete.id);
    setShowDeleteConfirm(false);

    try {
      await deleteSession(workspaceId, sessionToDelete.id);
      if (currentSession?.id === sessionToDelete.id) {
        const remaining = sessions.filter(s => s.id !== sessionToDelete.id);
        if (remaining.length > 0) {
          await loadSession(remaining[0].id);
        }
      }
    } catch (error) {
      console.error('删除会话失败:', error);
    } finally {
      setDeletingSessionId(null);
      setSessionToDelete(null);
    }
  }, [workspaceId, sessionToDelete, currentSession, sessions, deleteSession, loadSession]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDeleteConfirm) return;
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  useEffect(() => {
    if (isOpen && !sessionsLoaded && !loading) {
      fetchSessions(workspaceId, 1, 20);
    }
  }, [isOpen, sessionsLoaded, loading, workspaceId, fetchSessions]);

  const renderModals = () => (
    <>
      {showDeleteConfirm && sessionToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-sm p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">确认删除会话？</h3>
            <p className="text-sm text-gray-700 mb-4">
              您确定要删除会话 &ldquo;{sessionToDelete.title}&rdquo; 吗？此操作无法撤销。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (!isOpen) {
    return renderModals();
  }

  return (
    <>
      <div
        ref={containerRef}
        className="fixed w-96 max-h-96 bg-white border border-gray-200 rounded-lg shadow-sm z-50 flex flex-col"
        role="menu"
        aria-label="会话历史列表"
        style={{ top: '80px', right: '20px' }}
      >
        <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
          <h3 className="text-sm font-semibold text-gray-900">会话历史</h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sessions.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-600">暂无会话历史</p>
              <p className="text-xs text-gray-400 mt-1">点击上方 + 按钮创建新会话</p>
            </div>
          ) : (
            <ul className="py-2">
              {sessions.map((s) => {
                const isActive = s.id === currentSession?.id;
                const isDeleting = deletingSessionId === s.id;

                return (
                  <li
                    key={s.id}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-gray-50 border-l-4 border-purple-500'
                        : 'hover:bg-white border-l-4 border-transparent'
                    }`}
                    role="menuitem"
                    aria-selected={isActive}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => !isDeleting && handleSessionClick(s)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className={`text-sm font-medium truncate ${
                              isActive ? 'text-gray-900' : 'text-gray-800'
                            }`}>
                              {s.title || '未命名会话'}
                            </h4>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <ClockIcon />
                                <span>{formatRelativeTime(s.updated_at || s.created_at || '')}</span>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={(e) => handleDeleteClick(s, e)}
                            disabled={isDeleting}
                            className={`p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              isActive
                                ? 'text-red-300 hover:text-red-400 hover:bg-red-100 cursor-not-allowed'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title={isActive ? '无法删除当前正在使用的会话' : '删除会话'}
                            aria-label={`删除会话 ${s.title}`}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {renderModals()}
    </>
  );
};

export default AgentTeamSessionList;
